import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useContent } from "@/context/ContentContext";
import Countdown from "@/components/wedding/Countdown";

const ease = [0.16, 1, 0.3, 1];
const inputCls =
  "w-full bg-transparent border-0 border-b border-white/20 rounded-none py-3 text-white placeholder:text-white/35 focus:outline-none focus:border-white transition-colors duration-300 font-body";

export default function Invite() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const wedding = useContent();
  const [invite, setInvite] = useState(null);
  const [state, setState] = useState("loading"); // loading | ok | notfound
  const [attending, setAttending] = useState(null);
  const [guests, setGuests] = useState(1);
  const [song, setSong] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    document.title = "Boda de Caro y Roberto";
    (async () => {
      try {
        const { data } = await api.get(`/invite/${slug}`);
        setInvite(data);
        if (data.rsvp) {
          setAttending(data.rsvp.attending);
          setGuests(data.rsvp.guests || 1);
          setSong(data.rsvp.song || "");
          setMessage(data.rsvp.message || "");
          setDone(true);
        }
        setState("ok");
      } catch {
        setState("notfound");
      }
    })();
  }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    if (attending === null) return toast.error((invite?.max_guests || 1) > 1 ? "Indícanos si podrán asistir" : "Indícanos si podrás asistir");
    setLoading(true);
    try {
      await api.post(`/invite/${slug}/rsvp`, {
        attending,
        guests: attending ? Number(guests) || 1 : 0,
        song: song || null,
        message: message || null,
      });
      setDone(true);
      toast.success("¡Confirmación recibida! Gracias.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Error");
    } finally {
      setLoading(false);
    }
  };

  if (state === "loading")
    return <div className="min-h-screen bg-ink flex items-center justify-center text-white/50 overline">Cargando…</div>;

  if (state === "notfound")
    return (
      <div className="min-h-screen bg-ink flex flex-col items-center justify-center text-center px-6 grain relative">
        <p className="overline text-white/50">Invitación no encontrada</p>
        <h1 className="font-display text-4xl sm:text-5xl text-white mt-6">Enlace no válido</h1>
        <p className="text-white/60 font-light mt-4 max-w-sm">Verifica el enlace que te compartimos o contáctanos directamente.</p>
      </div>
    );

  const max = invite.max_guests || 1;
  const plural = max > 1;

  return (
    <div className="min-h-screen bg-ink text-white grain relative overflow-hidden">
      <div className="fixed inset-0 z-0">
        <video
          className="w-full h-full object-cover"
          src={wedding.media.heroVideo}
          poster={wedding.media.heroPoster}
          autoPlay
          loop
          muted
          playsInline
          data-testid="invite-bg-video"
        />
        <div className="absolute inset-0 bg-black/75" />
      </div>
      <div className="relative z-10 max-w-2xl mx-auto px-6 sm:px-10 py-20 sm:py-28">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, ease }}>
          <span className="overline text-white/50">— Nuestra Boda —</span>
          <h1 className="font-display text-5xl sm:text-7xl tracking-tight leading-[0.9] mt-6">
            {wedding.bride} <span className="italic">&amp;</span> {wedding.groom}
          </h1>
          <p className="font-display italic text-xl text-white/70 mt-4">{wedding.dateLabel}</p>

          {wedding.media.saveTheDateYouTubeId && (
            <div className="mt-10 mx-auto w-full max-w-xl" data-testid="invite-save-the-date-wrap">
              <span className="overline text-white/50 block text-center mb-4">— Save the Date —</span>
              <div className="relative w-full aspect-video bg-black/40 overflow-hidden border border-white/10">
                <iframe
                  src={`https://www.youtube.com/embed/${wedding.media.saveTheDateYouTubeId}?rel=0&modestbranding=1&playsinline=1&iv_load_policy=3&color=white`}
                  title="Save the Date — Carolina & Roberto"
                  className="absolute inset-0 w-full h-full"
                  frameBorder="0"
                  allow="encrypted-media; fullscreen; picture-in-picture"
                  allowFullScreen
                  data-testid="invite-save-the-date"
                />
              </div>
            </div>
          )}

          <div className="mt-10 border-t border-white/10 pt-8">
            <span className="overline text-white/50">Invitación para</span>
            <h2 className="font-display text-3xl sm:text-4xl mt-3" data-testid="invite-name">{invite.name}</h2>
            <p className="text-white/60 font-light mt-3">
              Nos encantaría contar con {plural ? "su" : "tu"} presencia. Esta invitación es válida para <strong className="text-white">{max}</strong> {plural ? "personas" : "persona"}.
            </p>
          </div>

          <div className="mt-10"><Countdown date={wedding.date} light compact showSeconds={false} /></div>
        </motion.div>

        {/* RSVP */}
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.15, ease }} className="mt-14 border-t border-white/10 pt-10">
          {done ? (
            <div className="border border-white/15 p-10 text-center" data-testid="invite-success">
              <div className="w-14 h-14 rounded-full border border-white/40 flex items-center justify-center mx-auto">
                <Check size={22} strokeWidth={1.4} />
              </div>
              <h3 className="font-display text-3xl mt-6">¡Gracias, {invite.name.split(" ")[0]}!</h3>
              <p className="text-white/60 font-light mt-3">
                {attending
                  ? `Hemos registrado ${plural ? "su" : "tu"} asistencia. ¡Nos vemos en la boda!`
                  : `Lamentamos que no pued${plural ? "an" : "as"} acompañarnos.`}
              </p>
              <button onClick={() => setDone(false)} className="mt-6 overline text-white/50 hover:text-white transition-colors" data-testid="invite-edit">
                Modificar respuesta
              </button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-8" data-testid="invite-rsvp-form">
              <div>
                <label className="overline text-white/50">{plural ? "¿Podrán acompañarnos?" : "¿Podrás acompañarnos?"}</label>
                <div className="flex gap-4 mt-4">
                  {[{ v: true, l: plural ? "Sí, asistiremos" : "Sí, asistiré" }, { v: false, l: plural ? "No podremos" : "No podré" }].map((opt) => (
                    <button key={String(opt.v)} type="button" onClick={() => setAttending(opt.v)}
                      className={`px-7 py-3 border transition-colors duration-300 overline ${attending === opt.v ? "bg-white text-ink border-white" : "border-white/25 text-white hover:border-white"}`}
                      data-testid={`invite-attending-${opt.v ? "yes" : "no"}`}>
                      {opt.l}
                    </button>
                  ))}
                </div>
              </div>

              {attending && max > 1 && (
                <div>
                  <label className="overline text-white/50">¿Cuántos asistirán? (máx. {max})</label>
                  <input type="number" min="1" max={max} value={guests} onChange={(e) => setGuests(e.target.value)} className={`${inputCls} mt-2`} data-testid="invite-guests" />
                </div>
              )}

              <input className={inputCls} placeholder="Sugiere una canción (opcional)" value={song} onChange={(e) => setSong(e.target.value)} data-testid="invite-song" />
              <textarea className={`${inputCls} resize-none`} rows={2} placeholder="Un mensaje para los novios (opcional)" value={message} onChange={(e) => setMessage(e.target.value)} data-testid="invite-message" />

              <button type="submit" disabled={loading}
                className="group inline-flex items-center gap-3 bg-white text-ink px-10 py-4 hover:bg-transparent hover:text-white border border-white transition-colors duration-300 disabled:opacity-50"
                data-testid="invite-submit">
                <span className="overline">{loading ? "Enviando…" : "Confirmar Asistencia"}</span>
              </button>
              <p className="mt-5 text-sm font-light text-white/50" data-testid="invite-deadline">
                Confirmar a más tardar el 31 de Diciembre 2026
              </p>
            </form>
          )}
        </motion.div>

        <div className="mt-14 border-t border-white/10 pt-10">
          <button onClick={() => navigate("/")} className="group inline-flex items-center gap-3 text-white" data-testid="invite-see-info">
            <span className="overline">Ver toda la información de la boda</span>
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1.5" />
          </button>
        </div>
      </div>
    </div>
  );
}
